import { schemaCompletionSource, type SQLDialect } from "@codemirror/lang-sql";
import { syntaxTree } from "@codemirror/language";
import type { SyntaxNode, Tree } from "@lezer/common";
import type { EditorState, Extension, Text } from "@uiw/react-codemirror";
import { type ModeId, populated, type Relation, type Schema } from "./engine";

// One row of the popup: the name, the icon it is drawn with, and the word in dimmer ink beside it.
export interface Option {
  label: string;
  // lang-sql's own three, so a name completed from here is drawn as one completed from the dialect's tables would be.
  type: "namespace" | "type" | "property";
  // What the pane would call it: a relation's kind, a column's type, and the word "schema" over both.
  detail: string;
  // The name as it has to be written where it cannot be written plainly, which the note on `quotingFor` explains.
  apply?: string;
  // Above the keywords, which lang-sql sinks to -1, and above the relations where a column is the likelier answer.
  boost?: number;
}

// The catalogue in the shape lang-sql walks: a schema holds relations and a relation holds columns, so a name
// completes a part at a time and `public.books.title` is three levels rather than one label with dots in it.
export type Catalogue = Record<string, SchemaScope>;

interface SchemaScope {
  self: Option;
  children: Record<string, RelationScope>;
}

interface RelationScope {
  self: Option;
  children: Option[];
}

// What one statement has to go on: the relations it names, and whether the caret is where another one would be
// written — after a FROM, a JOIN, an UPDATE or an INTO, which is the one place a column is not what is wanted.
export interface Scope {
  relations: string[][];
  naming: boolean;
}

// What the editor completes names from, read at the moment somebody asks rather than built into the extension: the
// catalogue is rewritten by every statement that runs, and an extension carrying one would be a new array after each
// one — which @uiw/react-codemirror answers with a reconfiguration, and a reconfiguration builds every state field
// again, the undo history among them. What is handed in is a reader, so the extensions around this one move with the
// dialect alone. There are two sources because there are two questions: what a name written in front of the caret
// holds, and what the statement itself has brought into scope, which is the only thing a bare `WHERE ` can answer
// from. Each is memoised on what it is built out of, so typing inside one clause rebuilds neither.
export function catalogueCompletion(dialect: SQLDialect, mode: ModeId, read: () => Schema[]): Extension {
  let catalogue: Schema[] | null = null;
  let named: Source = NOTHING;
  let scoped: Source = NOTHING;
  let scopedKey: string | null = null;

  // Every name the catalogue holds, a level at a time: `people`, `people.id` and `app.audit.at`, plus the relations
  // of the schema a bare name resolves against and the alias lang-sql reads off the statement for itself.
  const fromCatalogue: Source = (context) => {
    if (read() !== catalogue) refresh();
    return named(context);
  };

  // The columns of the relations this statement names, offered with no name in front of them — which is the whole of
  // what a WHERE clause, a SET or a select list has to go on. It answers nothing where a relation is being named,
  // and nothing at all after a dot: a source built from a flat list has no level to walk into, so the one above it
  // is left to answer `people.` alone rather than the two of them offering the same columns twice.
  const fromStatement: Source = (context) => {
    if (read() !== catalogue) refresh();
    const scope = statementScope(context.state, context.pos);
    if (scope.naming || scope.relations.length === 0) return null;

    const key = scope.relations.map((path) => path.join(".")).join(", ");
    if (key !== scopedKey) {
      scopedKey = key;
      const columns = scopeColumns(catalogue ?? [], mode, dialect, scope.relations);
      scoped = columns.length === 0 ? NOTHING : schemaCompletionSource({ dialect, schema: columns });
    }

    return scoped(context);
  };

  function refresh() {
    catalogue = read();
    named = sourceFor(dialect, mode, catalogue);
    scopedKey = null;
  }

  // Language data rather than an override, which is how lang-sql registers its own two and what keeps the keywords.
  return [
    dialect.language.data.of({ autocomplete: fromCatalogue }),
    dialect.language.data.of({ autocomplete: fromStatement }),
  ];
}

// The catalogue as the completion offers it. Every level carries a `self` of its own, since lang-sql would otherwise
// call a view and a table alike "type" and say nothing at all about a column beyond its name.
export function catalogueNamespace(schemas: Schema[], dialect: SQLDialect): Catalogue {
  const quoting = quotingFor(dialect);
  const catalogue: Catalogue = {};

  for (const schema of schemas) {
    const relations: Record<string, RelationScope> = {};

    for (const relation of schema.relations) {
      relations[key(relation.name)] = {
        self: option(relation.name, "type", relation.kind, quoting),
        children: relation.columns.map((column) => option(column.name, "property", column.type, quoting)),
      };
    }

    catalogue[key(schema.name)] = {
      self: option(schema.name, "namespace", "schema", quoting),
      children: relations,
    };
  }

  return catalogue;
}

// What the statement under the caret has named, read off the parse tree rather than off the text: `FROM`, `JOIN`,
// `UPDATE` and `INTO` are the four words a relation follows, a comma carries a FROM list on, and the identifier
// after a relation is its alias and never a second one. Everything a statement names is in scope wherever the caret
// is in it, subqueries included — a scope of one relation per clause would need a grammar that says which clause
// this is, and lang-sql's parses a statement as the flat run of tokens the tree shows.
export function statementScope(state: EditorState, pos: number): Scope {
  const scope: Scope = { relations: [], naming: false };
  const statement = statementAt(syntaxTree(state), pos);
  if (statement) read(statement, state.doc, pos, scope, { expecting: false, listing: false, found: false });
  return scope;
}

// The state the walk carries across a statement: whether the next name is a relation, whether a comma would begin
// another, and whether the caret has been passed — after which nothing more is said about where it was.
interface Reading {
  expecting: boolean;
  listing: boolean;
  found: boolean;
}

function read(node: SyntaxNode, doc: Text, pos: number, scope: Scope, at: Reading): void {
  for (let child = node.firstChild; child; child = child.nextSibling) {
    // A bracketed group is a column list or a subquery, and either way no comma inside it carries the list outside.
    if (GROUPED.has(child.name)) {
      at.expecting = false;
      at.listing = false;
      read(child, doc, pos, scope, at);
      at.listing = false;
      continue;
    }

    if (!at.found && child.from <= pos && pos <= child.to) {
      scope.naming = at.expecting;
      at.found = true;
    }

    if (child.name === "Keyword") {
      const word = doc.sliceString(child.from, child.to).toLowerCase();
      at.expecting = NAMES_RELATION.has(word);
      // `AS` stands between a relation and its alias, so it is the one word that leaves a FROM list open.
      at.listing = at.listing && word === "as";
    } else if (IDENTIFIER.test(child.name)) {
      if (at.expecting) {
        scope.relations.push(pathOf(child, doc));
        at.expecting = false;
        at.listing = true;
      }
    } else if (at.listing && child.name === "Punctuation" && doc.sliceString(child.from, child.to) === ",") {
      at.expecting = true;
    }

    if (!at.found && child.to <= pos) scope.naming = at.expecting;
  }
}

// The statement the caret is in, which is the one before it where a trailing space has left it just outside — unless
// a semicolon has closed that one, the caret then being in a statement nothing has been written in yet.
function statementAt(tree: Tree, pos: number): SyntaxNode | null {
  const inner = tree.resolveInner(pos, -1);
  for (let at: SyntaxNode | null = inner; at; at = at.parent) if (at.name === "Statement") return at;

  const before = inner.childBefore(pos);
  return before?.name === "Statement" && before.lastChild?.name !== ";" ? before : null;
}

// The four words a relation is written after, which are also the four places a column is not what is being asked for.
const NAMES_RELATION = new Set(["from", "join", "update", "into"]);

// `Identifier`, `QuotedIdentifier` and the `CompositeIdentifier` a dotted name is parsed as, which is lang-sql's test.
const IDENTIFIER = /Identifier$/;

const GROUPED = new Set(["Parens", "Braces", "Brackets"]);

// The schema an unqualified name resolves against, which is the one whose relations are offered without it: Postgres
// opens with `public` on the search path, and SQLite reads a bare name against `main` before anything attached.
const UNQUALIFIED: Record<ModeId, string> = { sqlite: "main", postgres: "public" };

// The source lang-sql builds from a catalogue, which is what both of the live ones above delegate to and rebuild.
type Source = ReturnType<typeof schemaCompletionSource>;

const NOTHING: Source = () => null;

// A database with nothing in it has no names to offer and answers with none, rather than with the schema Postgres
// reports whether or not anything is in it — the same `populated` the Load button asks about before it empties one.
function sourceFor(dialect: SQLDialect, mode: ModeId, schemas: Schema[]): Source {
  if (!populated(schemas)) return NOTHING;

  return schemaCompletionSource({
    dialect,
    schema: catalogueNamespace(schemas, dialect),
    defaultSchema: UNQUALIFIED[mode],
  });
}

// The columns of every relation the statement named, in the order it named them and each name only once: a name two
// of them share has to be qualified to be written at all, and offering it twice would say nothing about which is
// which. A relation the catalogue has never heard of contributes nothing rather than standing in the way.
function scopeColumns(schemas: Schema[], mode: ModeId, dialect: SQLDialect, relations: string[][]): Option[] {
  const quoting = quotingFor(dialect);
  const written = new Set<string>();
  const columns: Option[] = [];

  for (const path of relations) {
    for (const column of relationAt(schemas, mode, path)?.columns ?? []) {
      if (written.has(column.name)) continue;
      written.add(column.name);
      columns.push({ ...option(column.name, "property", column.type, quoting), boost: 1 });
    }
  }

  return columns;
}

// The relation a statement named: in the schema it was qualified with, or in the one a bare name resolves against —
// and failing that in whatever schema does hold it, since nothing here reads a `search_path` somebody has set.
function relationAt(schemas: Schema[], mode: ModeId, path: string[]): Relation | undefined {
  const name = path[path.length - 1];
  const within = path.length > 1 ? path[path.length - 2] : UNQUALIFIED[mode];

  return holds(schemas.find((schema) => schema.name === within), name)
    ?? (path.length > 1 ? undefined : schemas.map((schema) => holds(schema, name)).find(Boolean));
}

function holds(schema: Schema | undefined, name: string): Relation | undefined {
  return schema?.relations.find((relation) => relation.name === name);
}

function option(label: string, type: Option["type"], detail: string, quoting: Quoting): Option {
  return quoting.plain.test(label) ? { label, type, detail } : { label, type, detail, apply: quoting.wrap(label) };
}

interface Quoting {
  plain: RegExp;
  wrap: (label: string) => string;
}

// lang-sql quotes a name that cannot be written plainly, but only one it was handed as a string, and every name here
// arrives as an option of its own — so `userId` would be inserted where Postgres reads it as `userid` and refuses.
// The quote is the dialect's own first one, which is the one lang-sql would have reached for itself.
function quotingFor(dialect: SQLDialect): Quoting {
  const quote = dialect.spec.identifierQuotes?.[0] ?? "\"";
  const plain = new RegExp("^[a-z_][a-z_\\d]*$", dialect.spec.caseInsensitiveIdentifiers ? "i" : "");
  return { plain, wrap: (label) => `${quote}${label}${quote}` };
}

// A name is the text it was written as, less the quotes it was written in, which is how the catalogue holds it.
function pathOf(node: SyntaxNode, doc: Text): string[] {
  if (node.name !== "CompositeIdentifier") return [nameOf(node, doc)];

  const path: string[] = [];
  for (let part = node.firstChild; part; part = part.nextSibling) {
    if (part.name !== ".") path.push(nameOf(part, doc));
  }

  return path;
}

function nameOf(node: SyntaxNode, doc: Text): string {
  const text = doc.sliceString(node.from, node.to);
  return /^([`'"])(.*)\1$/.exec(text)?.[2] ?? text;
}

// A key is read as a path, so a dot inside a name of its own is escaped rather than left to open a level nothing is
// under: a table somebody called `v1.2` is one name and not a `v1` holding a `2`.
function key(name: string): string {
  return name.replace(/\./g, "\\.");
}
